import type { AttachmentLabels } from "./attachments/labels";

/** Every user-visible string the editor renders. Override any subset through the `labels` prop. */
export interface EditorLabels {
  editor: string;
  placeholder: string;
  loading: string;
  toolbar: string;
  bold: string;
  italic: string;
  strikethrough: string;
  code: string;
  link: string;
  /** The composer's formatting toolbar, named apart from the one that floats over a selection. */
  composerToolbar: string;
  numberedList: string;
  bulletedList: string;
  todoList: string;
  codeBlock: string;
  linkUrl: string;
  linkApply: string;
  linkRemove: string;
  linkInvalid: string;
  selectBlock: string;
  blockActions: string;
  /** `{count}` is replaced with the number of selected blocks. */
  blocksSelected: string;
  turnInto: string;
  moveUp: string;
  moveDown: string;
  /** Shown in the drag preview for a block with no text. */
  dragEmptyBlock: string;
  duplicate: string;
  copy: string;
  cut: string;
  deleteBlocks: string;
  suggestions: string;
  suggestionsLoading: string;
  suggestionsEmpty: string;
  suggestionsError: string;
  /** Shown when a paste was refused for its size. Nothing was pasted. */
  pasteTooLarge: string;
  /** Shown after a paste that was changed to fit. The text all came through. */
  pasteMergedCellsSplit: string;
  pasteNestedTableFlattened: string;
  pasteCellBlocksFlattened: string;
  pasteTableFlattened: string;
  pasteAttachmentFlattened: string;
  pasteImagesDropped: string;
  /**
   * What the attachment UI says, where it should differ from the defaults. Those live with the
   * attachment UI, which loads on demand, so an editor that never shows one never carries them.
   */
  attachments?: Partial<AttachmentLabels>;
  dismiss: string;
  /** The button on the cell the caret is in, which opens the table menu. */
  tableOptions: string;
  insertRowAbove: string;
  insertRowBelow: string;
  /** "Before" a column is where text starts from: left, unless the language reads right to left. */
  insertColumnBefore: string;
  insertColumnAfter: string;
  deleteRow: string;
  /** `{count}` is replaced with the number of selected rows. */
  deleteRows: string;
  deleteColumn: string;
  deleteColumns: string;
  headerRow: string;
  headerColumn: string;
  deleteTable: string;
  /** The composer's send button. */
  send: string;
  /** Its name while a send is waiting for the host. */
  sending: string;
  unreadableTitle: string;
  unreadableBody: string;
  exportOriginal: string;
  loadFailedTitle: string;
  retry: string;
}

export const DEFAULT_LABELS: EditorLabels = {
  editor: "Editor",
  placeholder: "Write something…",
  loading: "Loading…",
  toolbar: "Text formatting",
  bold: "Bold",
  italic: "Italic",
  strikethrough: "Strikethrough",
  code: "Inline code",
  link: "Link",
  composerToolbar: "Formatting",
  numberedList: "Numbered list",
  bulletedList: "Bulleted list",
  todoList: "To-do list",
  codeBlock: "Code block",
  linkUrl: "Link URL",
  linkApply: "Apply",
  linkRemove: "Remove link",
  linkInvalid: "That link type is not allowed.",
  selectBlock: "Select block",
  blockActions: "Block actions",
  blocksSelected: "{count} selected",
  turnInto: "Turn into",
  moveUp: "Move up",
  moveDown: "Move down",
  dragEmptyBlock: "Empty block",
  duplicate: "Duplicate",
  copy: "Copy",
  cut: "Cut",
  deleteBlocks: "Delete",
  suggestions: "Suggestions",
  suggestionsLoading: "Searching…",
  suggestionsEmpty: "No results",
  suggestionsError: "Suggestions could not be loaded.",
  pasteTooLarge: "That is too large to paste here, so nothing was pasted.",
  pasteMergedCellsSplit: "Merged cells were split into single cells. All the text was kept.",
  pasteNestedTableFlattened: "A table nested inside other content was pasted as text.",
  pasteCellBlocksFlattened: "Lists and headings inside cells were pasted as plain paragraphs.",
  pasteTableFlattened: "Tables are not available here, so the table was pasted as lines of text.",
  pasteAttachmentFlattened:
    "An attachment could not be pasted here, so its name was pasted instead.",
  pasteImagesDropped: "Images in the pasted content were left out. Add them as files instead.",
  dismiss: "Dismiss",
  tableOptions: "Table options",
  insertRowAbove: "Insert row above",
  insertRowBelow: "Insert row below",
  insertColumnBefore: "Insert column left",
  insertColumnAfter: "Insert column right",
  deleteRow: "Delete row",
  deleteRows: "Delete {count} rows",
  deleteColumn: "Delete column",
  deleteColumns: "Delete {count} columns",
  headerRow: "Header row",
  headerColumn: "Header column",
  deleteTable: "Delete table",
  send: "Send",
  sending: "Sending…",
  unreadableTitle: "This document can’t be opened here",
  unreadableBody:
    "Its content is stored safely and has not been changed. Editing is turned off so nothing is overwritten.",
  exportOriginal: "Download original",
  loadFailedTitle: "The document could not be loaded",
  retry: "Try again",
};
